import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Mail, Sparkles, Trash2, Edit, Loader2 } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import CreditUsageWidget from "@/components/CreditUsageWidget";
import { Modal, SandboxBanner } from "@/components/outreach/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const VARIABLES = ["{{first_name}}", "{{last_name}}", "{{company}}", "{{job_title}}"];

function TemplateEditor({ open, onClose, initial, onSaved }) {
    const [name, setName] = useState("");
    const [subject, setSubject] = useState("");
    const [bodyText, setBodyText] = useState("");
    const [saving, setSaving] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);

    useEffect(() => {
        setName(initial?.name || "");
        setSubject(initial?.subject || "");
        setBodyText(initial?.body_text || "");
    }, [initial, open]);

    const bodyHtml = "\n".concat(
        bodyText.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join(""),
    );

    const save = async () => {
        if (!name.trim() || !subject.trim() || !bodyText.trim()) {
            toast.error("Fill name, subject and body");
            return;
        }
        setSaving(true);
        try {
            const payload = { name, subject, body_html: bodyHtml, body_text: bodyText };
            if (initial?.id) {
                const { template } = await api.outreachTemplatePatch(initial.id, payload);
                onSaved(template, "updated");
            } else {
                const { template } = await api.outreachTemplateCreate(payload);
                onSaved(template, "created");
            }
            toast.success("Template saved");
        } catch (e) {
            toast.error(e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                title={initial?.id ? "Edit template" : "New template"}
                subtitle={"Variables: " + VARIABLES.join("  ")}
                maxWidth="max-w-3xl"
                testid="template-editor-modal"
            >
                <div className="space-y-4">
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">Name</label>
                        <input
                            data-testid="template-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                            placeholder="e.g. Cold intro SaaS"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">Subject</label>
                        <input
                            data-testid="template-subject-input"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                            placeholder="Quick idea for {{company}}"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between">
                            <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">Body</label>
                            <button
                                data-testid="template-ai-compose-btn"
                                onClick={() => setAiOpen(true)}
                                className="inline-flex items-center gap-1 text-xs text-[var(--color-secondary)] hover:opacity-80"
                            >
                                <Sparkles className="h-3.5 w-3.5" />
                                Write with AI
                            </button>
                        </div>
                        <textarea
                            data-testid="template-body-input"
                            value={bodyText}
                            onChange={(e) => setBodyText(e.target.value)}
                            rows={10}
                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm font-mono focus:outline-none focus:border-[var(--color-primary)]/60"
                            placeholder="Hi {{first_name}}…"
                        />
                        <p className="mt-1 text-[10px] text-text-secondary">
                            {bodyText.length} chars · Variables:{" "}
                            {VARIABLES.map((v) => (
                                <button
                                    key={v}
                                    onClick={() => setBodyText((t) => t + " " + v)}
                                    className="mr-1 rounded px-1.5 py-0.5 bg-[var(--color-panel-ai)] hover:bg-[var(--color-primary)]/20 transition-colors"
                                >{v}</button>
                            ))}
                        </p>
                    </div>

                    {/* Preview */}
                    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3">
                        <p className="text-[10px] uppercase tracking-widest text-text-secondary/80">Preview (variables un-substituted)</p>
                        <p className="mt-2 text-sm font-medium">{subject || "(no subject)"}</p>
                        <p className="mt-1 text-xs text-text-secondary whitespace-pre-wrap">{bodyText || "(no body)"}</p>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            onClick={onClose}
                            className="rounded-full border border-[var(--color-border)] px-4 py-2 text-sm text-text-secondary hover:text-text-main"
                        >Cancel</button>
                        <button
                            data-testid="template-save-btn"
                            onClick={save}
                            disabled={saving}
                            className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2 text-sm font-medium text-white disabled:opacity-60"
                        >
                            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Save
                        </button>
                    </div>
                </div>
            </Modal>

            <AiComposeModal
                open={aiOpen}
                onClose={() => setAiOpen(false)}
                onCompose={(data) => {
                    setSubject(data.subject);
                    setBodyText(data.body_text);
                    setAiOpen(false);
                    toast.success(`AI draft generated · ${data.credits_spent} credit`);
                }}
            />
        </>
    );
}

function AiComposeModal({ open, onClose, onCompose }) {
    const [goal, setGoal] = useState("book a demo call");
    const [tone, setTone] = useState("friendly");
    const [audience, setAudience] = useState("SaaS decision-makers");
    const [loading, setLoading] = useState(false);

    const generate = async () => {
        if (!goal.trim()) return;
        setLoading(true);
        try {
            const data = await api.outreachAiCompose({
                goal: goal.trim(), tone, audience_hint: audience,
            });
            onCompose(data);
        } catch (e) {
            if (e.status === 402) toast.error("Insufficient credits");
            else if (e.status === 429) toast.error("Hourly credit cap reached");
            else toast.error(e.message || "AI compose failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Write with AI"
            subtitle="1 credit · gpt-4o-mini"
            testid="ai-compose-modal"
        >
            <div className="space-y-3">
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">Goal</label>
                    <input
                        data-testid="ai-goal-input"
                        value={goal} onChange={(e) => setGoal(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                        placeholder="e.g. book a demo call"
                    />
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">Tone</label>
                    <input
                        data-testid="ai-tone-input"
                        value={tone} onChange={(e) => setTone(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                    />
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">Audience</label>
                    <input
                        data-testid="ai-audience-input"
                        value={audience} onChange={(e) => setAudience(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                    />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="rounded-full border border-[var(--color-border)] px-4 py-2 text-sm text-text-secondary hover:text-text-main">
                        Cancel
                    </button>
                    <button
                        data-testid="ai-generate-btn"
                        onClick={generate} disabled={loading}
                        className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2 text-sm font-medium text-white disabled:opacity-60"
                    >
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        Generate
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default function OutreachTemplates() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [meta, setMeta] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [tpls, m] = await Promise.all([api.outreachTemplates(), api.outreachMeta()]);
            setItems(tpls.items || []);
            setMeta(m);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this template?")) return;
        try {
            await api.outreachTemplateDelete(id);
            setItems((cur) => cur.filter((t) => t.id !== id));
            toast.success("Deleted");
        } catch (e) {
            toast.error(e.message || "Delete failed");
        }
    };

    const onSaved = (tpl, mode) => {
        setEditorOpen(false);
        setEditing(null);
        setItems((cur) => {
            if (mode === "created") return [tpl, ...cur];
            return cur.map((t) => (t.id === tpl.id ? tpl : t));
        });
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main data-testid="templates-main" className="flex-1 px-4 md:px-6 py-6 max-w-5xl w-full">
                    <SandboxBanner show={meta?.sandbox} />
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                        <div>
                            <p className="text-[11px] uppercase tracking-widest text-text-secondary">Templates</p>
                            <h1 className="mt-1 text-2xl md:text-3xl font-semibold tracking-tight">
                                Le tue{" "}
                                <span className="gradient-text">email riutilizzabili</span>
                            </h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <CreditUsageWidget />
                            <button
                                data-testid="template-new-btn"
                                onClick={() => { setEditing(null); setEditorOpen(true); }}
                                className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98]"
                            >
                                <Plus className="h-4 w-4" />
                                New template
                            </button>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                        {loading ? (
                            <div className="p-6 text-sm text-text-secondary">Loading…</div>
                        ) : items.length === 0 ? (
                            <div data-testid="templates-empty" className="p-12 text-center">
                                <div className="mx-auto h-14 w-14 rounded-full bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                    <Mail className="h-5 w-5 text-[var(--color-secondary)]" />
                                </div>
                                <h3 className="mt-4 text-base font-semibold">Nessun template</h3>
                                <p className="mt-2 text-sm text-text-secondary">Crea il primo e sblocca la creazione di sequenze.</p>
                            </div>
                        ) : (
                            <ul>
                                {items.map((t, i) => (
                                    <motion.li
                                        key={t.id}
                                        initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, delay: i * 0.02 }}
                                        data-testid={`template-row-${i}`}
                                        className="flex items-center justify-between border-b border-[var(--color-border)] last:border-b-0 px-5 py-3.5 hover:bg-[var(--color-panel-ai)]/50 transition-colors"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium truncate">{t.name}</p>
                                            <p className="text-xs text-text-secondary truncate">{t.subject}</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                data-testid={`template-edit-${i}`}
                                                onClick={() => { setEditing(t); setEditorOpen(true); }}
                                                className="rounded-md p-1.5 text-text-secondary hover:text-text-main hover:bg-[var(--color-panel-ai)]"
                                                title="Edit"
                                            ><Edit className="h-4 w-4" /></button>
                                            <button
                                                data-testid={`template-delete-${i}`}
                                                onClick={() => remove(t.id)}
                                                className="rounded-md p-1.5 text-text-secondary hover:text-[var(--color-alert)] hover:bg-[var(--color-alert)]/10"
                                                title="Delete"
                                            ><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </motion.li>
                                ))}
                            </ul>
                        )}
                    </div>
                </main>
            </div>

            <TemplateEditor
                open={editorOpen}
                onClose={() => { setEditorOpen(false); setEditing(null); }}
                initial={editing}
                onSaved={onSaved}
            />
        </div>
    );
}
